import type { FlagValue, InstanceReport } from '../../api';
import { formatDateTime, isStale, relativeTime } from '../../format';
import { viewReport } from '../../reportView';
import { formatValue } from '../FlagsTab';
import { Alert } from '../Alert';
import { STATE_LABELS, STATE_TONES } from '../labels';
import { Tag } from '../Tag';

/** Au-delà de ce délai sans rapport, l'agent est considéré comme silencieux (il rapporte toutes les 5 min environ). */
export const STALE_REPORT_MS = 15 * 60_000;

export function ReportPanel({
  report,
  desiredRevision,
  manifestFlags,
  now,
}: {
  report: InstanceReport | null;
  desiredRevision: number | null;
  manifestFlags: Record<string, FlagValue> | null;
  now: number;
}) {
  if (!report) {
    return (
      <div className="empty">
        <p>Aucun rapport reçu.</p>
        <p className="muted">
          L'agent n'a pas encore contacté WHCC pour cette instance. Émettez un jeton d'agent, puis vérifiez
          que l'instance le lit (redémarrage de l'instance après une pose dans le cluster).
        </p>
      </div>
    );
  }

  const v = viewReport(report.report);
  const stale = isStale(report.receivedAt, now, STALE_REPORT_MS);
  const behind = desiredRevision !== null && report.appliedRevision !== desiredRevision;
  const flagEntries = Object.entries(v.effectiveFlags).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className={`stack${stale ? ' report-stale' : ''}`} data-stale={stale ? 'true' : 'false'}>
      <div className="row-actions">
        <Tag tone={STATE_TONES[report.state]}>{STATE_LABELS[report.state]}</Tag>
        <span title={formatDateTime(report.receivedAt)}>
          Rapport reçu {relativeTime(report.receivedAt, now)}
        </span>
        {stale && <Tag tone="orange">Périmé</Tag>}
        {v.restartPending && <Tag tone="orange">Redémarrage en attente</Tag>}
      </div>
      {stale && (
        <Alert kind="warning">
          Le dernier rapport a été reçu {relativeTime(report.receivedAt, now)} : l'agent ne répond peut-être
          plus (instance arrêtée, jeton révoqué ou WHCC injoignable depuis l'instance). Les valeurs ci-dessous
          peuvent être obsolètes.
        </Alert>
      )}
      {v.lastApplyError && (
        <Alert>
          Dernière erreur d'application : <span className="url">{v.lastApplyError}</span>
        </Alert>
      )}

      <dl className="meta">
        <div>
          <dt>DSH</dt>
          <dd>{v.dshVersion ?? '—'}</dd>
        </div>
        <div>
          <dt>Agent</dt>
          <dd>{v.agentVersion ?? '—'}</dd>
        </div>
        <div>
          <dt>Image</dt>
          <dd>{v.imageVersion ?? '—'}</dd>
        </div>
        <div>
          <dt>Révision appliquée / désirée</dt>
          <dd>
            {report.appliedRevision ?? 'aucune'} / {desiredRevision ?? '—'}{' '}
            {desiredRevision !== null &&
              (behind ? <Tag tone="orange">En retard</Tag> : <Tag tone="green">À jour</Tag>)}
          </dd>
        </div>
        <div>
          <dt>Dernière activité de l'instance</dt>
          <dd>{v.lastActivityAt ? relativeTime(v.lastActivityAt, now) : 'inconnue'}</dd>
        </div>
        <div>
          <dt>Dérive</dt>
          <dd>{v.drift.detected ? <Tag tone="orange">Détectée</Tag> : <Tag tone="green">Aucune</Tag>}</dd>
        </div>
      </dl>

      <h3>Plugins chargés</h3>
      {v.plugins.length === 0 ? (
        <p className="muted">Aucun plugin rapporté.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Plugins rapportés par l'agent</caption>
            <thead>
              <tr>
                <th scope="col">Paquet</th>
                <th scope="col">Version</th>
                <th scope="col">Activé</th>
                <th scope="col">Phase</th>
                <th scope="col">Erreur</th>
              </tr>
            </thead>
            <tbody>
              {v.plugins.map((p) => (
                <tr key={p.package}>
                  <th scope="row">
                    <code>{p.package}</code>
                  </th>
                  <td>{p.version}</td>
                  <td>{p.enabled ? 'Oui' : 'Non'}</td>
                  <td>
                    <Tag tone={p.error ? 'red' : p.phase === 'active' ? 'green' : 'gray'}>{p.phase}</Tag>
                  </td>
                  <td className="url">{p.error ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Erreurs récentes</h3>
      {v.recentErrors.length === 0 ? (
        <p className="muted">Aucune erreur récente.</p>
      ) : (
        <ul className="plain-list">
          {v.recentErrors.map((e, i) => (
            <li key={i}>
              <span className="muted nowrap">{e.at ? formatDateTime(e.at) : '—'}</span>
              {e.scope && <code className="chip">{e.scope}</code>} <span className="url">{e.message}</span>
            </li>
          ))}
        </ul>
      )}

      <h3>Dérive</h3>
      {v.drift.items.length === 0 ? (
        <p className="muted">
          {v.drift.detected ? 'Dérive signalée sans détail.' : 'Aucune dérive détectée.'}
        </p>
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Éléments en dérive</caption>
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Élément</th>
                <th scope="col">Attendu</th>
                <th scope="col">Constaté</th>
              </tr>
            </thead>
            <tbody>
              {v.drift.items.map((d, i) => (
                <tr key={i}>
                  <td>{d.kind}</td>
                  <th scope="row">
                    <code>{d.name}</code>
                  </th>
                  <td>{d.expected ?? '—'}</td>
                  <td>{d.actual ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Flags effectifs</h3>
      {flagEntries.length === 0 ? (
        <p className="muted">Aucun flag rapporté.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Flags tels qu'appliqués par l'instance</caption>
            <thead>
              <tr>
                <th scope="col">Flag</th>
                <th scope="col">Valeur effective</th>
                <th scope="col">Dans le manifeste</th>
              </tr>
            </thead>
            <tbody>
              {flagEntries.map(([key, value]) => {
                const expected = manifestFlags ? manifestFlags[key] : undefined;
                const differs = manifestFlags !== null && expected !== value;
                return (
                  <tr key={key}>
                    <th scope="row">
                      <code>{key}</code>
                    </th>
                    <td>{formatValue(value)}</td>
                    <td>
                      {manifestFlags === null ? (
                        '—'
                      ) : differs ? (
                        <Tag tone="orange">{expected === undefined ? 'absent' : formatValue(expected)}</Tag>
                      ) : (
                        <span className="muted">identique</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
